import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  alpha,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import { useColorMode } from "../../../theme/ThemeContext";
import TableComponent from "../admincomponents/Table";
import {
  People as PeopleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";

const GroupManagement = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const GOLD = "#D4AF37";

  const [groups, setGroups] = useState([
    {
      id: 1,
      name: "Web Development Cohort",
      description: "A group for students learning web development",
      category: "Study Group",
      members: 24,
      createdAt: "2023-01-15",
      membersList: [
        { id: 1, name: "John Doe", email: "john@example.com", role: "Student" },
        {
          id: 2,
          name: "Jane Smith",
          email: "jane@example.com",
          role: "Instructor",
        },
        {
          id: 3,
          name: "Bob Johnson",
          email: "bob@example.com",
          role: "Student",
        },
      ],
    },
    {
      id: 2,
      name: "Data Science Club",
      description: "For data science enthusiasts",
      category: "Club",
      members: 18,
      createdAt: "2023-02-10",
      membersList: [
        {
          id: 4,
          name: "Alice Brown",
          email: "alice@example.com",
          role: "Student",
        },
        {
          id: 5,
          name: "Charlie Wilson",
          email: "charlie@example.com",
          role: "Instructor",
        },
      ],
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [membersDialog, setMembersDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [currentGroupMembers, setCurrentGroupMembers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "Study Group",
  });

  const filteredGroups = groups.filter(
    (group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const columns = [
    {
      key: "name",
      label: "Group Name",
      sortable: true,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "text.primary" }}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            maxWidth: "300px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.description}
        </Typography>
      ),
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      render: (row) => (
        <Chip
          label={row.category}
          size="small"
          variant="outlined"
          sx={{
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: "text.primary",
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      key: "members",
      label: "Members",
      align: "center",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.members}
        </Typography>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {row.createdAt}
        </Typography>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleMembersClick(row)}
            sx={{
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
            }}
            title="View Members"
          >
            <PeopleIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEditClick(row)}
            sx={{
              color: "#7366ff",
              bgcolor: alpha("#7366ff", 0.1),
              "&:hover": { bgcolor: alpha("#7366ff", 0.2) },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(row)}
            sx={{
              color: "#ff5370",
              bgcolor: alpha("#ff5370", 0.1),
              "&:hover": { bgcolor: alpha("#ff5370", 0.2) },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleOpenDialog = () => {
    setIsEditing(false);
    setFormData({
      name: "",
      description: "",
      category: "Study Group",
    });
    setOpenDialog(true);
  };

  const handleEditClick = (group) => {
    setIsEditing(true);
    setCurrentId(group.id);
    setFormData(group);
    setOpenDialog(true);
  };

  const handleMembersClick = (group) => {
    setCurrentGroupMembers(group.membersList);
    setMembersDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!formData.name || !formData.description) {
      alert("Please fill in all required fields");
      return;
    }

    if (isEditing) {
      setGroups(
        groups.map((g) => (g.id === currentId ? { ...g, ...formData } : g)),
      );
    } else {
      setGroups([
        ...groups,
        {
          ...formData,
          id: Date.now(),
          members: 0,
          createdAt: new Date().toISOString().split("T")[0],
          membersList: [],
        },
      ]);
    }

    handleCloseDialog();
  };

  const handleDeleteClick = (group) => {
    setCurrentId(group.id);
    setDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setGroups(groups.filter((g) => g.id !== currentId));
    setDeleteConfirm(false);
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* Header with Theme Toggle */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
            fontFamily: "'Outfit', sans-serif"
          }}
        >
          Group <Box component="span" sx={{ color: GOLD }}>Management</Box>
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                input: {
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000" }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
                boxShadow: "0 4px 15px rgba(212, 175, 55, 0.3)",
                color: "#0D0D0D",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                  boxShadow: "0 6px 20px rgba(212, 175, 55, 0.5)",
                },
              }}
            >
              Create Group
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableComponent
          columns={columns}
          data={filteredGroups}
          pagination={true}
          itemsPerPage={10}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: theme.palette.text.primary }}
        >
          {isEditing ? "Edit Group" : "Create New Group"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              select
              label="Category"
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              SelectProps={{
                native: true,
              }}
            >
              <option value="Study Group">Study Group</option>
              <option value="Club">Club</option>
              <option value="Workshop">Workshop</option>
              <option value="Project Team">Project Team</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog
        open={membersDialog}
        onClose={() => setMembersDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: theme.palette.text.primary }}
        >
          Group Members
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {currentGroupMembers.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentGroupMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.role}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: theme.palette.text.secondary }}>
              No members in this group
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle sx={{ color: theme.palette.text.primary }}>
          Delete Group
        </DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.primary }}>
          Are you sure you want to delete this group? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupManagement;
//                   cursor: "pointer",
//                 }}
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Group Members Modal */}
//       {showMembersModal && (
//         <div style={modalBackdropStyle}>
//           <div style={{ ...modalContentStyle, width: "600px" }}>
//             <h2 style={{ marginBottom: "20px" }}>Members of {currentGroup?.name}</h2>

//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//                 marginBottom: "20px",
//               }}
//             >
//               <div>Total Members: {currentGroup?.members}</div>
//               <button
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "5px",
//                   padding: "8px 15px",
//                   background: "linear-gradient(135deg, #ff5baf, #7366ff)",
//                   color: "white",
//                   border: "none",
//                   borderRadius: "5px",
//                   cursor: "pointer",
//                 }}
//               >
//                 <FaUserPlus />
//                 Add Member
//               </button>
//             </div>

//             <Table columns={membersColumns} data={currentGroup?.membersList || []} pagination={false} />

//             <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
//               <button
//                 onClick={() => setShowMembersModal(false)}
//                 style={{
//                   padding: "8px 15px",
//                   background: "#f5f5f5",
//                   border: "1px solid #ddd",
//                   borderRadius: "5px",
//                   cursor: "pointer",
//                 }}
//               >
//                 Close
//               </button>
//             </div>
